import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Filter, ShoppingBag, Music, Video, Smartphone, Globe, Image, Wrench } from "lucide-react";
import { api } from "@/lib/api";
import Header from "@/components/marketing/Header";
import Footer from "@/components/marketing/Footer";
import { useLang } from "@/lib/lang";

const typeFilters = [
  { id: "", label: "mkt_all", icon: Globe },
  { id: "app", label: "mkt_apps", icon: Smartphone },
  { id: "music", label: "mkt_music", icon: Music },
  { id: "video", label: "mkt_videos", icon: Video },
  { id: "media", label: "mkt_media", icon: Image },
  { id: "service", label: "mkt_services_filter", icon: Wrench },
];

export default function Marketplace() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const { t } = useLang();

  useEffect(() => {
    setLoading(true);
    api.getMarketplaceProducts(filter)
      .then(setProducts)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filter]);

  const filtered = search
    ? products.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || p.description.toLowerCase().includes(search.toLowerCase()))
    : products;

  return (
    <div className="min-h-screen bg-[#05050A]" data-testid="marketplace-page">
      <Header />
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
            <p className="label-mono mb-4">{t("mkt_label")}</p>
            <h1 className="font-['Unbounded'] text-4xl sm:text-5xl font-black tracking-tighter text-white mb-4">
              {t("mkt_heading_1")}<span className="bg-gradient-to-r from-[#00F0FF] to-[#FF007F] bg-clip-text text-transparent">{t("mkt_heading_2")}</span>
            </h1>
            <p className="text-[#A0A0AB] text-base sm:text-lg max-w-2xl">{t("mkt_subtitle")}</p>
          </motion.div>

          {/* Search & Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-10">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0A0AB]" />
              <input
                data-testid="marketplace-search"
                type="text"
                placeholder={t("mkt_search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-2xl pl-12 pr-4 py-3 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/50 transition-all"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {typeFilters.map((tf) => (
                <button
                  key={tf.id}
                  data-testid={`filter-${tf.id || 'all'}`}
                  onClick={() => setFilter(tf.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    filter === tf.id
                      ? "bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30"
                      : "bg-white/5 text-[#A0A0AB] border border-white/10 hover:text-white"
                  }`}
                >
                  <tf.icon className="w-4 h-4" />
                  {t(tf.label)}
                </button>
              ))}
            </div>
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="glass-card rounded-3xl p-12 text-center max-w-lg mx-auto">
              <ShoppingBag className="w-12 h-12 text-[#00F0FF] mx-auto mb-4" />
              <h3 className="font-['Unbounded'] text-xl font-bold text-white mb-2">{t("mkt_no_products")}</h3>
              <p className="text-[#A0A0AB] text-sm mb-6">{t("mkt_no_products_text")}</p>
              <Link to="/creator" className="neon-btn text-sm py-2 px-6">{t("mkt_become_creator")}</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((product, i) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link
                    to={`/marketplace/${product.id}`}
                    data-testid={`product-card-${product.id}`}
                    className="block glass-card rounded-2xl overflow-hidden group hover:-translate-y-1 transition-all duration-300 hover:border-[#00F0FF]/30"
                  >
                    {product.image_url && (
                      <div className="h-44 overflow-hidden">
                        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    )}
                    <div className="p-5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#00F0FF]">{product.product_type}</span>
                        {product.price > 0 && (
                          <span className="text-white font-bold text-lg">${product.price.toFixed(2)}</span>
                        )}
                        {product.price === 0 && (
                          <span className="text-[#00F0FF] font-bold text-sm">FREE</span>
                        )}
                      </div>
                      <h3 className="font-['Unbounded'] text-base font-semibold text-white mb-1 truncate">{product.name}</h3>
                      <p className="text-[#A0A0AB] text-xs line-clamp-2 mb-3">{product.description}</p>
                      {product.creator?.name && (
                        <p className="text-[#A0A0AB]/60 text-xs">by {product.creator.company_name || product.creator.name}</p>
                      )}
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="mt-16 text-center">
            <div className="glass-card rounded-3xl p-10 max-w-2xl mx-auto">
              <h3 className="font-['Unbounded'] text-2xl font-bold text-white mb-3">{t("mkt_sell_title")}</h3>
              <p className="text-[#A0A0AB] text-sm mb-6">{t("mkt_sell_text")}</p>
              <div className="flex gap-4 justify-center flex-wrap">
                <Link to="/pricing" data-testid="marketplace-pricing-link" className="neon-btn text-sm py-2 px-6">View Pricing</Link>
                <Link to="/creator" data-testid="marketplace-creator-link" className="neon-btn neon-btn-pink text-sm py-2 px-6">Become a Creator</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
